import { useState } from "react";
import { useFragment } from "react-relay";
import graphql from "babel-plugin-relay/macro";
import styled from "styled-components";

import { MapZoomControl } from "./Map";
import { Portal } from "./Map/MapPortal";
import { FieldDetailsFragment$key } from "./__generated__/FieldDetailsFragment.graphql";
import Accordion from "./Buttons/Accordion";
import Button from "./Buttons/Button";
import IconButton from "./Buttons/IconButton";
import MeasurementModal from "./MeasurementModal";
import FieldDetailsAttributes from "./FieldDetails.attributes";
import FieldDetailsReports from "./FieldDetails.reports";
import FieldMapContainer from "./FieldMap";
import FieldDetailsLabels from "./FieldDetails.labels";

interface FieldDetailsProps {
  field: FieldDetailsFragment$key;
}

const FieldDetails = ({ field }: FieldDetailsProps) => {
  const [showMeasurementModal, setShowMeasurementModal] = useState(false);
  const [enableMeasurement, setEnableMeasurement] = useState(true);
  const [openAccordion, setOpenAccordion] = useState<
    "intekening" | "meldingen" | "labels"
  >("intekening");
  const data = useFragment(
    graphql`
      fragment FieldDetailsFragment on FieldNode {
        id
        ...FieldDetailsAttributesFragment
        ...FieldDetailsReportsFragment
        ...FieldDetailsLabelsFragment
        ...FieldMap
      }
    `,
    field
  );
  function openMeasurementModal(enable: boolean): void {
    setEnableMeasurement(enable);
    setShowMeasurementModal(true);
  }
  return (
    <FieldDetailsContainer>
      <FieldDetailsBody>
        <FieldDetailsTitle>Details beheereenheid</FieldDetailsTitle>
        <FieldDetailsInformationContainer>
          <AccordionContainer>
            <Accordion
              title="Intekening"
              onClick={() => setOpenAccordion("intekening")}
              isOpen={openAccordion === "intekening"}
            >
              <FieldDetailsAttributes fieldData={data} />
            </Accordion>
            <Accordion
              title="Meldingen"
              onClick={() => setOpenAccordion("meldingen")}
              isOpen={openAccordion === "meldingen"}
            >
              <FieldDetailsReports fieldData={data} />
            </Accordion>
            <Accordion
              title="Labels"
              onClick={() => setOpenAccordion("labels")}
              isOpen={openAccordion === "labels"}
            >
              <FieldDetailsLabels fieldData={data} />
            </Accordion>
          </AccordionContainer>
          {!showMeasurementModal ? (
            <MapContainer>
              <FieldMapContainer key={`detail_map_${data.id}`} field={data}>
                <MapZoomControl />
                <Portal>
                  <div
                    style={{
                      display: "flex",
                      gap: "1rem",
                      position: "absolute",
                      right: 8,
                      top: 8,
                    }}
                  >
                    <Button
                      icon="pencil"
                      label="Afstand meten"
                      size="medium"
                      onClick={() => openMeasurementModal(true)}
                    />
                    <IconButton
                      icon="expand"
                      size="large"
                      tooltip="Vergroot kaart"
                      onClick={() => openMeasurementModal(false)}
                    />
                  </div>
                </Portal>
              </FieldMapContainer>
            </MapContainer>
          ) : null}
        </FieldDetailsInformationContainer>
      </FieldDetailsBody>
      {showMeasurementModal ? (
        <MeasurementModal
          field={data}
          enableMeasurement={enableMeasurement}
          onDismiss={() => setShowMeasurementModal(false)}
        />
      ) : null}
    </FieldDetailsContainer>
  );
};

const FieldDetailsContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const FieldDetailsBody = styled.div`
  width: 70vw;
  background: white;
  margin-top: 38px;
  padding: 18px 22px;
  filter: drop-shadow(0px 10px 15px rgba(168, 168, 168, 0.2));

  /* Dropshadow */
  border-radius: 5px;
  @media only screen and (max-width: 480px) {
    margin: unset;
    overflow: unset;
    height: 100%;
    width: 100%;
  }
`;

const FieldDetailsTitle = styled.h4`
  color: var(--Donkergrijs, #212529);
  font-family: Roboto;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%; /* 21.6px */
  padding: 0;
  margin: 0;
`;

const FieldDetailsInformationContainer = styled.div`
  margin-top: 17px;
  padding: 20px;
  background: #fafafa;
  height: 370px;
  flex-direction: row;
  display: flex;
`;

const AccordionContainer = styled.div`
  width: 390px;
`;

const MapContainer = styled.div`
  margin-left: 15px;
  width: 100%;
`;

const Title = styled.span`
  color: #000;
  font-family: Roboto;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  font-size: 10px;
  text-transform: uppercase;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 260px;
`;

const DetailText = styled.span`
  color: var(--Donkergrijs, #212529);
  font-family: Roboto;
  font-style: normal;
  font-size: 12px;

  font-weight: 700;
  line-height: 120%;
`;

const DetailValue = styled.span`
  width: 100px;
  color: var(--Donkergrijs, #212529);

  /* Text small */
  font-family: Roboto;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;

  line-height: 120%; /* 14.4px */
`;

const DetailsContainer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export { Title, DetailsContainer, DetailRow, DetailText, DetailValue };

export default FieldDetails;
